import { useState, useCallback, useEffect } from 'react';
import {
  calculateNumberFrequency,
  generateHotAndWild,
  generateBalanced,
  generatePatternBased,
  generatePatternFilteredNumbers,
  generatePurelyRandom,
  getNextDrawNumber,
} from '../../utils/lotto';

// 1. 데이터 소스 정의
const DATA_URL = 'https://enzo2-lotto-data.s3.ap-northeast-2.amazonaws.com/lotto-history-enhanced.json';

// JSON 데이터의 타입을 명시적으로 선언합니다.
export interface LottoDraw {
  drawNumber: number;
  drawDate: string;
  numbers: number[];
  stats: {
    totalSum: number;
    oddEvenRatio: string;
  };
}

export type StrategyType =
  | '자주 나오는 수 + 의외의 수'
  | '골고루 조합'
  | '인기 + 연속수 조합'
  | '통계 패턴 필터링'
  | '완전 무작위'
  | 'null';

export interface HistoryEntry {
  numbers: number[];
  timestamp: string;
  strategy: StrategyType;
  drawNumber: number;
}

const ITEMS_PER_PAGE = 10;

export const useLottoPage = () => {
  // 2. 상태 관리
  const [lottoData, setLottoData] = useState<LottoDraw[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [recommendedNumbers, setRecommendedNumbers] = useState<number[]>([]);
  const [strategy, setStrategy] = useState<StrategyType>('null');
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [history, setHistory] = useState<HistoryEntry[]>(() => {
    try {
      const savedHistory = localStorage.getItem('lottoHistory');
      return savedHistory ? JSON.parse(savedHistory) : [];
    } catch (error) {
      console.error("로또 기록을 localStorage에서 불러오는 데 실패했습니다.", error);
      return [];
    }
  });

  // 3. 데이터 불러오기
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(DATA_URL);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: LottoDraw[] = await response.json();
        setLottoData(data);
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError('알 수 없는 에러가 발생했습니다.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []); // 컴포넌트 마운트 시 한 번만 실행

  useEffect(() => {
    try {
      localStorage.setItem('lottoHistory', JSON.stringify(history));
    } catch (error) {
      console.error("로또 기록을 localStorage에 저장하는 데 실패했습니다.", error);
    }
  }, [history]);

  const addHistoryEntry = useCallback((numbers: number[], strategy: StrategyType) => {
    const drawNumber = getNextDrawNumber();
    setHistory((prevHistory) => [
      { numbers, timestamp: new Date().toLocaleString(), strategy, drawNumber },
      ...prevHistory,
    ]);
    setCurrentPage(1);
  }, []);

  const generateNumbers = useCallback((type: StrategyType) => {
    if (!lottoData) return;

    const frequency = calculateNumberFrequency(lottoData);

    setIsGenerating(true);
    setStrategy(type);

    let newNumbers: number[] = [];
    switch (type) {
      case '자주 나오는 수 + 의외의 수':
        newNumbers = generateHotAndWild(frequency);
        break;
      case '골고루 조합':
        newNumbers = generateBalanced(frequency);
        break;
      case '인기 + 연속수 조합':
        newNumbers = generatePatternBased(frequency);
        break;
      case '통계 패턴 필터링':
        newNumbers = generatePatternFilteredNumbers(lottoData);
        break;
      case '완전 무작위':
        newNumbers = generatePurelyRandom();
        break;
      default:
        setIsGenerating(false);
        return;
    }
    setRecommendedNumbers(newNumbers);
    addHistoryEntry(newNumbers, type);
    setIsGenerating(false);
  }, [addHistoryEntry, lottoData]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const paginatedHistory = history.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return {
    // 데이터 로딩 관련 상태 반환
    lottoData,
    isLoading,
    error,
    // 기존 상태 및 함수 반환
    recommendedNumbers,
    history,
    paginatedHistory,
    strategy,
    isGenerating,
    generateNumbers,
    currentPage,
    pageCount: Math.ceil(history.length / ITEMS_PER_PAGE),
    handlePageChange,
  };
};